import { NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import { AgendaHttpService } from '@sc/data-access-http-services';
import { Dossier, LocationDataGeocoded, country } from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import {
  CheckboxComponent,
  InputTextComponent,
  PhoneInputComponent,
} from '@sc/ui-components';
import { Observable, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { PlanningSandboxService } from '../../../services';
import { PlanningFormAddressFieldComponent } from '../planning-form-address-field/planning-form-address-field.component';

@Component({
  selector: 'sc-planning-form-appointment-customer-info',
  styleUrls: ['./planning-form-appointment-customer-info.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    InputTextComponent,
    PlanningFormAddressFieldComponent,
    NgIf,
    TranslateModule,
    CheckboxComponent,
    PhoneInputComponent,
  ],
  template: `
    <ng-container [formGroup]="getCustomerForm()">
      <fieldset>
        <legend>
          {{ 'PlanningFormAppointmentCustomerInfo.title' | translate }}
        </legend>
        <div class="form-row">
          <div class="form-data">
            <sc-input-text
              id="customerLastNameInput"
              [label]="
                'PlanningFormAppointmentCustomerInfo.lastnameLabel' | translate
              "
              [suggestionLabelFunc]="suggestionLabelFunc"
              [suggestionsLabelName]="'customerName'"
              [suggestions]="dossierSearchResult"
              (updateSuggestions)="onUpdateSuggestions($event)"
              (selectSuggestion)="onSelectSuggestion($event)"
              formControlName="lastname"
            ></sc-input-text>
          </div>
          <div class="form-data">
            <sc-input-text
              id="customerFirstNameInput"
              [label]="
                'PlanningFormAppointmentCustomerInfo.firstnameLabel' | translate
              "
              formControlName="firstname"
            ></sc-input-text>
          </div>
        </div>
        <div class="form-row">
          <div class="form-data">
            <ng-container formGroupName="phoneNumber">
              <sc-phone-input
                class="input"
                [value]="
                  _form?.get('customer')?.get('phoneNumber')?.get('number')
                    ?.value
                "
                formControlName="number"
                [label]="
                  'PlanningFormAppointmentCustomerInfo.phoneLabel' | translate
                "
                [setCountry]="phoneCountry"
                [displayCountrySelection]="false"
                (changeCountry)="countryChange($event)"
              ></sc-phone-input>
            </ng-container>
          </div>
          <div class="form-data">
            <sc-input-text
              id="customerEmailInput"
              [label]="
                'PlanningFormAppointmentCustomerInfo.emailLabel' | translate
              "
              formControlName="email"
              [hint]="
                'GlobalApplicativeConstants.accessibility.labels.emailExemple'
                  | translate
              "
            ></sc-input-text>
          </div>
        </div>

        <div class="form-row">
          <div class="form-data">
            <sc-planning-form-address-field
              [searchLocationData]="
                _form?.get('customer')?.get('location')?.value
              "
              [disabled]="_form?.disabled"
              (changeAddress)="onChangeAddress($event)"
            ></sc-planning-form-address-field>

            <sc-checkbox
              *ngIf="
                featureFlipManager.isFeatureActivated(
                  'agenda-diff-billing-address'
                )
              "
              [value]="!isBillingAddrSameAsClientAddr"
              [disabled]="_form?.disabled"
              (changeValue)="onChangeIsSameBillingAddr($event)"
            >
              {{
                'PlanningFormAppointmentCustomerInfo.differentBillingAddr'
                  | translate
              }}
            </sc-checkbox>
          </div>
        </div>

        <div
          class="form-row"
          *ngIf="
            !isBillingAddrSameAsClientAddr &&
            featureFlipManager.isFeatureActivated('agenda-diff-billing-address')
          "
        >
          <sc-planning-form-address-field
            [searchLocationData]="
              _form?.get('customer')?.get('billingLocation')?.value
            "
            [disabled]="_form?.disabled"
            (changeAddress)="onChangeBillingAddress($event)"
          ></sc-planning-form-address-field>
        </div>

        <div class="form-row" *ngIf="canDisplayVatNumber()">
          <div
            class="form-data vat-number-container"
            [formGroup]="getPolicyInformationForm()"
          >
            <sc-input-text
              id="customerVatNumberInput"
              [label]="
                'PlanningFormAppointmentCustomerInfo.vatNumberLabel' | translate
              "
              formControlName="vatNumber"
            ></sc-input-text>
          </div>
        </div>
      </fieldset>
    </ng-container>
  `,
})
export class PlanningFormAppointmentCustomerInfoComponent {
  _form: FormGroup;
  @Input() set form(newForm: FormGroup) {
    this.onInitNewForm(newForm);
  }
  @Input() set touched(value) {
    if (value) this._form?.get('customer').markAllAsTouched();
  }
  @Output() loadDossierEvent = new EventEmitter<Dossier>();

  dossierSearchResult = [];

  phoneCountry: country;

  isBillingAddrSameAsClientAddr = true;

  formClientAddress$: Observable<LocationDataGeocoded>;
  formBillingAddress$: Observable<LocationDataGeocoded>;

  formSubscriptions = new Subscription();

  constructor(
    private readonly agendaHttpService: AgendaHttpService,
    private readonly planningSandbox: PlanningSandboxService,
    private readonly configService: ConfigurationService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    public featureFlipManager: FeatureFlippingManagerService,
    private readonly translator: TranslatorService
  ) {}

  onInitNewForm(newForm: FormGroup) {
    this.formSubscriptions.unsubscribe();
    this.formSubscriptions = new Subscription();

    this._form = newForm;

    if (this._form.get('id')?.value) {
      this.phoneCountry = this._form
        ?.get('customer')
        ?.get('phoneNumber')
        ?.get('countryCode')?.value;
    } else {
      this.phoneCountry = this.configService.getDefaultPhoneNumberCountry();
    }

    const subFitterIdChange = this._form
      .get('appointment')
      ?.get('fitterId')
      ?.valueChanges?.subscribe(() => (this.dossierSearchResult = []));

    const subClientAddressChange = this._form
      .get('customer')
      ?.get('location')
      ?.valueChanges.subscribe((newLoc) => {
        if (this.isBillingAddrSameAsClientAddr) {
          this.onChangeBillingAddress(newLoc);
        }
      });

    const subDetectChanges = this._form.valueChanges.subscribe(() => {
      this.changeDetectorRef.markForCheck();
    });

    this.formSubscriptions.add(subDetectChanges);
    this.formSubscriptions.add(subFitterIdChange);
    this.formSubscriptions.add(subClientAddressChange);

    this.isBillingAddrSameAsClientAddr =
      this._form.get('customer')?.get('location')?.value?.placeId ===
      this._form.get('customer')?.get('billingLocation')?.value?.placeId;

    this.changeDetectorRef.markForCheck();
  }

  countryChange(value) {
    this._form?.patchValue({
      customer: {
        phoneNumber: {
          countryCode: value,
        },
      },
    });
    this.phoneCountry = value;
    this.changeDetectorRef.markForCheck();
  }

  getCustomerForm() {
    return this._form?.get('customer') as FormGroup;
  }

  getPolicyInformationForm() {
    return this._form?.get('policyInformation') as FormGroup;
  }

  canDisplayVatNumber() {
    return this.featureFlipManager.isFeatureActivated(
      'agenda-display-vat-number'
    );
  }

  onUpdateSuggestions(value: string) {
    if (this._form.get('id')?.value?.length) return;
    const fitterId = this._form.get('appointment')?.get('fitterId')?.value;
    const brandId = this._form.get('appointment')?.get('brandId')?.value;
    if (fitterId) {
      if (!!value && value.length > 2) {
        this.agendaHttpService
          .searchDossiersByCustomerName({
            customerName: value,
            brandId,
            fitterId,
          })
          .pipe(debounceTime(500))
          .subscribe((results) => {
            if (results?.length) {
              this.dossierSearchResult = results;
              this.changeDetectorRef.markForCheck();
            } else {
              this.dossierSearchResult = [];
              this.dossierSearchResult.push({
                id: null,
                label: this.translator.getInstantTranslation(
                  'PlanningFormAppointmentCustomerInfo.autocompleteNoResultOption'
                ),
                disabled: true,
              });
            }
          });
      } else {
        this.dossierSearchResult = [];
      }
    } else {
      this.dossierSearchResult = [];
      this.dossierSearchResult.push({
        id: null,
        label: this.translator.getInstantTranslation(
          'PlanningFormAppointmentCustomerInfo.autocompleteSelectFitterOption'
        ),
        disabled: true,
      });
    }
  }

  onSelectSuggestion(value) {
    this.planningSandbox.fetchDossier(value.id)?.subscribe(
      (dossier) => {
        dossier.claim.car.vehicleType =
          dossier?.claim?.car?.vehicleType ?? 'car';
        if (
          !dossier?.appointment?.brandId &&
          !!dossier?.requestAppointment?.brandId
        ) {
          this.loadDossierEvent.emit(dossier);
        } else {
          this._form?.patchValue({
            customer: {
              ...dossier?.customer,
            },
            policyInformation: {
              vatNumber: dossier?.policyInformation?.vatNumber ?? null,
            },
            claim: {
              car: { ...dossier?.claim?.car },
            },
          });
          this.phoneCountry = dossier?.customer?.phoneNumber
            ?.countryCode as country;
          this.planningSandbox.updateAutoCompleteCarSelection(
            dossier?.claim?.car
          );
          this.changeDetectorRef.markForCheck();
        }
      },
      (error) => console.log(error)
    );
  }

  suggestionLabelFunc(option) {
    let labelToReturn = option?.customerName ?? option?.label;

    if (option?.customerFirstName && !option?.label) {
      labelToReturn += ' ' + option?.customerFirstName;
    }
    if (option?.licensePlateNumber && !option?.label) {
      labelToReturn += ' - ' + option?.licensePlateNumber;
    }
    return labelToReturn ?? '';
  }

  onChangeAddress(newLocation: LocationDataGeocoded) {
    this._form?.get('customer')?.get('location').setValue(newLocation);
  }

  onChangeIsSameBillingAddr(isChecked: boolean) {
    this.isBillingAddrSameAsClientAddr = !isChecked;

    if (this.isBillingAddrSameAsClientAddr) {
      this.onChangeBillingAddress(
        this._form.get('customer')?.get('location')?.value
      );
    } else {
      this.onChangeBillingAddress(null);
    }
  }

  onChangeBillingAddress(newLocation: LocationDataGeocoded) {
    this._form?.get('customer')?.get('billingLocation').setValue(newLocation);
  }
}
